import type { NotificationItem, ReturnRequestDetail } from '../../types/vipMp';

export function pick(row: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!row) return '';
  const lower = Object.fromEntries(Object.entries(row).map(([k, v]) => [k.toLowerCase(), v]));
  for (const key of keys) {
    const value = row[key] ?? lower[key.toLowerCase()];
    if (value != null && String(value).trim() !== '') return String(value);
  }
  return '';
}

export function partnerFacingStatus(sub: Record<string, unknown>): string {
  const code = pick(sub, ['StatusCode', 'StageProcessStatus', 'CurrentStatusCode'])
    .toUpperCase()
    .replace(/-/g, '_');
  const name = pick(sub, ['StatusName', 'Status', 'RequestStatusLabel']).toLowerCase();
  if (code === 'ON_HOLD' || name.includes('on hold')) return 'In Progress';
  if (code === 'PENDING_OM_ACTION' || name.includes('pending om action')) return 'In Progress';
  if (code === 'PENDING_BP_OPS_ACTION' || name.includes('pending bp ops')) return 'In Progress';
  if (code === 'PENDING_PARTNER_ACTION' || name.includes('needs reply') || name.includes('pending partner')) {
    return 'Pending Partner Action';
  }
  if (code === 'APPROVED' || name.includes('approved')) return 'Approved';
  if (code === 'REJECTED' || name.includes('rejected')) return 'Rejected';
  if (code === 'NEW') return 'New';
  return pick(sub, ['StatusName', 'Status', 'RequestStatusLabel', 'CurrentStatusCode']) || 'N/A';
}

export function statusClassName(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized.includes('pending partner') || normalized.includes('need') || normalized.includes('reply')) return 'status-needs-reply';
  if (normalized.includes('approve')) return 'status-approved';
  if (normalized.includes('reject')) return 'status-rejected';
  if (normalized.includes('resolve') || normalized.includes('closed')) return 'status-resolved';
  if (normalized === 'new') return 'status-new';
  return 'status-in-progress';
}

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function parseDate(value: unknown): Date | null {
  if (value == null || String(value).trim() === '') return null;
  const s = String(value);
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(parseInt(dateOnly[1]), parseInt(dateOnly[2]) - 1, parseInt(dateOnly[3]));
  const date = new Date(s);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: unknown): string {
  const date = parseDate(value);
  if (!date) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  return `${dd} ${MONTH_ABBR[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatDateTime(value: unknown): string {
  const date = parseDate(value);
  if (!date) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const datePart = `${dd} ${MONTH_ABBR[date.getMonth()]} ${date.getFullYear()}`;
  const h = date.getHours();
  const m = String(date.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${datePart}, ${h12}:${m} ${ampm}`;
}

export function formatMoney(amount: unknown, _currency?: string): string {
  const value = typeof amount === 'number' ? amount : Number.parseFloat(String(amount ?? ''));
  if (Number.isNaN(value)) return '-';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function pickNumber(row: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  const value = pick(row, keys);
  if (!value) return null;
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function detailHeader(detail: ReturnRequestDetail): Record<string, unknown> {
  return (detail.header || {}) as Record<string, unknown>;
}

export function resolveRequestNumber(row: Record<string, unknown> | null | undefined): string {
  return pick(row, ['RequestNumber', 'requestNumber', 'Adobe_Sales_Order_Number', 'adobeSalesOrderNumber']);
}

export function resolveReturnRequestId(row: Record<string, unknown> | null | undefined): number {
  return Number.parseInt(pick(row, ['ReturnRequestId', 'returnRequestId', 'Return_Request_ID']), 10);
}

export function notificationTitle(item: NotificationItem): string {
  return String(item.title ?? item.type ?? 'Update');
}

export function notificationBody(item: NotificationItem): string {
  return String(item.body ?? item.message ?? '');
}
